/** Sorteo público de grupos de Round Robin (Eduardo 2026-09-26).
 *
 * Serpentina por seed: con G grupos, los seeds 1..G van a los grupos 1..G,
 * los seeds G+1..2G regresan de G a 1, y así sucesivamente (1A 2B 3C 4D,
 * 5D 6C 7B 8A, 9A…). Misma regla que el backend (groups/draw.py). */
export function snakeGroupIndex(position: number, groupCount: number): number {
  if (groupCount <= 0) throw new Error("groupCount must be positive");
  const cycle = Math.floor(position / groupCount);
  const offset = position % groupCount;
  return cycle % 2 === 0 ? offset : groupCount - 1 - offset;
}

/** Aviso al mover jugadores en una categoría ya sorteada (decisión 3). */
export const POST_DRAW_CHANGE_NOTICE =
  "Esta categoría ya se sorteó. El cambio se permite, pero quedará registrado en el acta pública del sorteo.";

export const GROUP_DRAW_ACTION_LABELS: Record<string, string> = {
  assigned: "Asignado",
  moved: "Movido",
  removed: "Sacado del grupo",
};

/** Barajado sin sesgo (Fisher–Yates). `sort(() => Math.random() - 0.5)`
 * no reparte parejo. */
export function shuffled<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Bloqueos "de estructura": con ellos la tarjeta del sorteo no se muestra
 * (no aplica o ya no se puede), aunque sigue informando el estado. */
export function isStructuralDrawBlocker(reason: string): boolean {
  return (
    reason.includes("solo aplica a categorías de Round Robin") ||
    reason.includes("al menos 2 grupos") ||
    reason.includes("ya tiene partidos") ||
    reason.includes("por equipos")
  );
}

export interface SlotGroup {
  id: number;
  capacity?: number | null;
}

/** Reparto automático del paso Grupos (puro, para poder probarlo).
 *
 * `ordered` ya viene en orden: en "Por rank" los sembrados por seed y luego
 * los S/R barajados; en "Random" todos barajados. Con `snakeSeeded` los
 * sembrados van en serpentina (1A 2B 3C 4D, 5D 6C 7B 8A…) saltando grupos
 * llenos; los demás (y los sembrados cuando los grupos ya tienen gente) van
 * al grupo con menos jugadores, al azar entre empatados. */
export function planGroupSlots(
  groups: readonly SlotGroup[],
  initialCounts: Readonly<Record<number, number>>,
  ordered: ReadonlyArray<{ id: number; seeded: boolean }>,
  options: { snakeSeeded: boolean; random?: () => number },
): Array<{ participantId: number; groupId: number }> {
  const random = options.random ?? Math.random;
  const counts: Record<number, number> = {};
  groups.forEach((group) => {
    counts[group.id] = initialCounts[group.id] ?? 0;
  });
  const hasRoom = (group: SlotGroup) =>
    group.capacity == null || group.capacity <= 0 || counts[group.id] < group.capacity;
  const plan: Array<{ participantId: number; groupId: number }> = [];
  let pointer = 0;
  for (const item of ordered) {
    const available = groups.filter(hasRoom);
    if (available.length === 0) break;
    let target: SlotGroup | undefined;
    if (options.snakeSeeded && item.seeded) {
      for (let attempt = 0; attempt < groups.length * 2; attempt += 1) {
        const candidate = groups[snakeGroupIndex(pointer, groups.length)];
        pointer += 1;
        if (hasRoom(candidate)) {
          target = candidate;
          break;
        }
      }
    }
    if (!target) {
      const minCount = Math.min(...available.map((group) => counts[group.id]));
      const lightest = available.filter((group) => counts[group.id] === minCount);
      target = lightest[Math.floor(random() * lightest.length)];
    }
    plan.push({ participantId: item.id, groupId: target.id });
    counts[target.id] += 1;
  }
  return plan;
}

